'use client';

import { Evidence } from '@heidi/design-system/icons';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getOnboardingHost } from '../../../../onboarding-host';

const FALLBACK_GHOST_QUERY =
  'What are CSANZ chest pain guidelines for a 50 year old caucasian male?';

interface LayoutProps {
  query: string;
  onQueryChange: (v: string) => void;
  onSubmit: () => void;
  submitted: boolean;
  loading: boolean;
}

function useAutoType(
  onQueryChange: (v: string) => void,
  submitted: boolean,
  query: string,
  ghost: string
) {
  const [autoTyping, setAutoTyping] = useState(true);
  const [done, setDone] = useState(false);
  const charRef = useRef(0);

  useEffect(() => {
    if (!autoTyping || submitted) return;
    if (charRef.current >= ghost.length) {
      setAutoTyping(false);
      setDone(true);
      return;
    }
    const t = setTimeout(
      () => {
        charRef.current += 1;
        onQueryChange(ghost.slice(0, charRef.current));
      },
      55 + Math.random() * 45
    );
    return () => clearTimeout(t);
  }, [autoTyping, submitted, onQueryChange, query, ghost]);

  const interrupt = (val: string) => {
    setAutoTyping(false);
    charRef.current = val.length;
    onQueryChange(val);
  };
  return { autoTyping, done, interrupt };
}

/** Guided typing indicator — user avatar typing with cursor. */
function GuidedTypingLayout({
  query,
  onQueryChange,
  onSubmit,
  submitted,
  loading,
}: LayoutProps) {
  const intl = useIntl();
  const host = getOnboardingHost();
  const specialtyAssetName = host.useSelectedSpecialtyCanonicalName();
  const { data: assets } = host.useQuerySpecialtyOnboardingV2Assets(
    specialtyAssetName,
    intl.locale
  );
  const ghostQuery = assets?.evidence_queries?.[0] || FALLBACK_GHOST_QUERY;
  const { autoTyping, done, interrupt } = useAutoType(
    onQueryChange,
    submitted,
    query,
    ghostQuery
  );

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-text-primary text-[10px] font-bold text-text-primary-invert">
          <FormattedMessage
            defaultMessage="You"
            id="wEUV98S3oY"
            description="Avatar label marking the user's own message in the Evidence demo"
          />
        </div>
        <span className="text-xs text-text-tertiary">
          {autoTyping ? (
            <FormattedMessage
              defaultMessage="Typing a query to Evidence"
              id="eUhcRvJdH8"
              description="Status while the demo auto-types an Evidence query"
            />
          ) : done ? (
            <FormattedMessage
              defaultMessage="Your query is ready"
              id="OuS7b9ZVep"
              description="Status once the demo Evidence query has finished typing"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Type your query"
              id="Eh2hWuU9u0"
              description="Prompt to type an Evidence query in the demo"
            />
          )}
        </span>
      </div>
      <div
        className="cursor-text rounded-xl border border-border bg-surface px-4 py-3 shadow-sm"
        onClick={() => inputRef.current?.focus()}
      >
        <input
          ref={inputRef}
          className="absolute opacity-0"
          value={query}
          onChange={(e) => interrupt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSubmit();
          }}
          disabled={submitted}
        />
        <span className="text-sm text-text-primary">{query}</span>
        {(autoTyping || (!done && !submitted)) && (
          <span className="ml-px inline-block h-4 w-0.5 animate-[pulse_0.5s_ease-in-out_infinite] bg-text-primary align-text-bottom" />
        )}
      </div>
      {done && !submitted && (
        <div className="relative self-start">
          <span className="absolute -inset-0.5 animate-ping rounded-lg border border-accent-forest" />
          <button
            type="button"
            onClick={onSubmit}
            className="relative flex items-center gap-2 rounded-lg bg-accent-forest px-4 py-2 text-xs font-medium text-white"
          >
            <Evidence size={14} className="text-white" />{' '}
            <FormattedMessage
              defaultMessage="Use Evidence"
              id="dcn1z1LQqY"
              description="Button submitting the typed query to Evidence in the demo"
            />
          </button>
        </div>
      )}
      {loading && (
        <div className="flex items-center gap-2 text-xs text-text-tertiary">
          <Loader2 className="size-3 animate-spin" />{' '}
          <FormattedMessage
            defaultMessage="Querying evidence"
            id="bGJoJZ7jVf"
            description="Loading status while the demo Evidence query runs"
          />
        </div>
      )}
    </div>
  );
}

export function EvidenceLayoutRenderer(props: LayoutProps) {
  return <GuidedTypingLayout {...props} />;
}
